'use client';

import { useCallback } from 'react';
import {
	SpotMarketConfig,
	TokenFaucet,
	BN,
	SpotMarketAccount,
	IWallet,
} from '@drift-labs/sdk';
import { Connection, PublicKey, LAMPORTS_PER_SOL } from '@solana/web3.js';

import { getTokenAddressForDepositAndWithdraw } from '@drift/common';
import useDriftClient from 'src/hooks/useDriftClient';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import { notify } from 'src/utils/notifications';

/**
 * Hook for requesting test token airdrops.
 */
export const useFaucetAirdrop = () => {
	const driftClient = useDriftClient();
	const connection = useDriftStore((s) => s.connection.current);
	const wallet = useDriftStore((s) => s.wallet.current.adapter);

	const tryFaucetAirdrop = useCallback(
		async (spotMarket: SpotMarketConfig) => {
			if (!driftClient || !connection || !wallet?.publicKey) {
				console.error('Missing required dependencies for airdrop');
				return;
			}

			const notificationId = `airdrop${Date.now()}`;

			try {
				let airdropAmount = 10000;

				switch (spotMarket.symbol) {
					case 'SOL':
						airdropAmount = 1;
						break;
					case 'BTC':
						airdropAmount = 0.1;
						break;
					default:
						break;
				}

				notify({
					type: 'info',
					message: `Airdropping ${airdropAmount} ${spotMarket.symbol}`,
					id: notificationId,
					showUntilCancelled: true,
				});

				const isSol = spotMarket.symbol === 'SOL';

				if (isSol) {
					await connection.requestAirdrop(
						wallet.publicKey,
						airdropAmount * LAMPORTS_PER_SOL
					);
				} else {
					const faucet = new TokenFaucet(
						connection as Connection,
						wallet as IWallet,
						new PublicKey('V4v1mQiAdLz4qwckEb45WqHYceYizoib39cDBHSWfaB'),
						spotMarket.mint
					);

					const airdropAmountBn = new BN(
						airdropAmount * 10 ** spotMarket.precisionExp.toNumber()
					);

					const spotMarketAccount = driftClient.getSpotMarketAccount(
						spotMarket.marketIndex
					);

					const expectedTokenAccountAddress =
						await getTokenAddressForDepositAndWithdraw(
							spotMarketAccount as SpotMarketAccount,
							wallet.publicKey
						);

					const tokenAccountInfo = await connection.getAccountInfo(
						expectedTokenAccountAddress
					);

					const tokenAccountExists = !!tokenAccountInfo;

					if (tokenAccountExists) {
						await faucet.mintToUser(
							expectedTokenAccountAddress,
							airdropAmountBn
						);
					} else {
						await faucet.createAssociatedTokenAccountAndMintTo(
							wallet.publicKey,
							airdropAmountBn
						);
					}
				}

				notify({
					type: 'success',
					message: `Airdropped ${airdropAmount} ${spotMarket.symbol}`,
					id: notificationId,
					updatePrevious: true,
				});
			} catch (e: any) {
				console.error(e);
				notify({
					type: 'error',
					message: `Airdrop failed`,
					description:
						'Please try again. If this problem persists contact the Drift team',
					id: notificationId,
					updatePrevious: true,
				});
			}
		},
		[driftClient, connection, wallet]
	);

	return { tryFaucetAirdrop };
};
